import { createClerkSupabaseClient } from '../lib/supabase.js';

const emptyDayRange = (days) => {
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    result.push({ date: date.toISOString().split('T')[0], sessions: 0 });
  }
  return result;
};

// Helper function to get supabase client
const getSupabaseClient = async () => {
  const session = window.Clerk?.session;
  
  if (!session) {
    throw new Error('Usuario no autenticado con Clerk');
  }
  
  return createClerkSupabaseClient(session);
};

export const sesionesService = {
  // Obtener sesiones agrupadas por día para el gráfico
  // En la línea ~25, mejorar el manejo del error:
  async getSessionsByDay(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const supabase = await getSupabaseClient();
      
      // Usar directamente la consulta fallback en lugar de intentar RPC
      const { data, error } = await supabase
        .from('sesiones')
        .select('created_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });
  
      if (error) {
        console.error('Error fetching sessions:', error);
        return emptyDayRange(days);
      }

      // Agrupar sesiones por día (método original)
      const sessionsByDay = {};

      data.forEach(session => {
        const date = new Date(session.created_at).toISOString().split('T')[0];
        sessionsByDay[date] = (sessionsByDay[date] || 0) + 1;
      });

      // Crear array con todos los días del rango
      const result = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        result.push({
          date: dateStr,
          sessions: sessionsByDay[dateStr] || 0
        });
      }

      return result;
    } catch (error) {
      console.error('Error in getSessionsByDay:', error);
      return emptyDayRange(days);
    }
  },

  // Obtener sesiones con simulación agrupadas por día
  async getSessionsWithSimulationByDay(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const supabase = await getSupabaseClient();
      
      const { data, error } = await supabase
        .from('sesiones')
        .select('created_at, datos_simulacion')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });
  
      if (error) {
        console.error('Error fetching sessions with simulation:', error);
        return emptyDayRange(days);
      }

      // Filtrar sesiones que tienen datos de simulación
      const sessionsWithSimulation = data.filter(session => 
        session.datos_simulacion && 
        Object.keys(session.datos_simulacion).length > 0
      );

      // Agrupar sesiones por día
      const sessionsByDay = {};
      
      sessionsWithSimulation.forEach(session => {
        const date = new Date(session.created_at).toISOString().split('T')[0];
        sessionsByDay[date] = (sessionsByDay[date] || 0) + 1;
      });

      // Crear array con todos los días del rango
      const result = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        result.push({
          date: dateStr,
          sessions: sessionsByDay[dateStr] || 0
        });
      }

      return result;
    } catch (error) {
      console.error('Error in getSessionsWithSimulationByDay:', error);
      return emptyDayRange(days);
    }
  },

  // Obtener planes cotizados agrupados por día
  async getQuotedPlansByDay(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const supabase = await getSupabaseClient();
      
      const { data, error } = await supabase
        .from('sesiones')
        .select('created_at, datos_dps')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });
  
      if (error) {
        console.error('Error fetching quoted plans:', error);
        return emptyDayRange(days);
      }

      // Filtrar sesiones que tienen cotización
      const sessionsWithQuotation = data.filter(session => 
        session.datos_dps && 
        session.datos_dps.cotizacion && 
        Object.keys(session.datos_dps.cotizacion).length > 0
      );

      // Agrupar sesiones por día
      const sessionsByDay = {};
      
      sessionsWithQuotation.forEach(session => {
        const date = new Date(session.created_at).toISOString().split('T')[0];
        sessionsByDay[date] = (sessionsByDay[date] || 0) + 1;
      });

      // Crear array con todos los días del rango
      const result = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        result.push({
          date: dateStr,
          sessions: sessionsByDay[dateStr] || 0
        });
      }

      return result;
    } catch (error) {
      console.error('Error in getQuotedPlansByDay:', error);
      return emptyDayRange(days);
    }
  },

  // Obtener estadísticas generales de sesiones
  async getSessionsStats() {
    try {
      const supabase = await getSupabaseClient();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

      // Sesiones de hoy
      const { data: todaySessions, error: todayError } = await supabase
        .from('sesiones')
        .select('id', { count: 'exact' })
        .gte('created_at', today.toISOString().split('T')[0]);

      // Sesiones de ayer
      const { data: yesterdaySessions, error: yesterdayError } = await supabase
        .from('sesiones')
        .select('id', { count: 'exact' })
        .gte('created_at', yesterday.toISOString().split('T')[0])
        .lt('created_at', today.toISOString().split('T')[0]);

      // Sesiones de este mes
      const { data: thisMonthSessions, error: thisMonthError } = await supabase
        .from('sesiones')
        .select('id', { count: 'exact' })
        .gte('created_at', thisMonth.toISOString());

      // Sesiones del mes pasado
      const { data: lastMonthSessions, error: lastMonthError } = await supabase
        .from('sesiones')
        .select('id', { count: 'exact' })
        .gte('created_at', lastMonth.toISOString())
        .lte('created_at', lastMonthEnd.toISOString());

      if (todayError || yesterdayError || thisMonthError || lastMonthError) {
        throw todayError || yesterdayError || thisMonthError || lastMonthError;
      }

      const todayCount = todaySessions?.length || 0;
      const yesterdayCount = yesterdaySessions?.length || 0;
      const thisMonthCount = thisMonthSessions?.length || 0;
      const lastMonthCount = lastMonthSessions?.length || 0;

      // Calcular cambios porcentuales
      const dailyChange = yesterdayCount > 0 
        ? ((todayCount - yesterdayCount) / yesterdayCount * 100).toFixed(1)
        : todayCount > 0 ? 100 : 0;

      const monthlyChange = lastMonthCount > 0
        ? ((thisMonthCount - lastMonthCount) / lastMonthCount * 100).toFixed(1)
        : thisMonthCount > 0 ? 100 : 0;

      return {
        today: todayCount,
        yesterday: yesterdayCount,
        thisMonth: thisMonthCount,
        lastMonth: lastMonthCount,
        dailyChange: parseFloat(dailyChange),
        monthlyChange: parseFloat(monthlyChange)
      };
    } catch (error) {
      console.error('Error in getSessionsStats:', error);
      throw error;
    }
  },

  // Obtener sesiones por canal
  async getSessionsByChannel(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const supabase = await getSupabaseClient();
      
      const { data, error } = await supabase
        .from('sesiones')
        .select('canal')
        .gte('created_at', startDate.toISOString());

      if (error) {
        console.error('Error fetching sessions by channel:', error);
        throw error;
      }

      // Agrupar por canal
      const channelCounts = {};
      data.forEach(session => {
        const channel = session.canal || 'Sin canal';
        channelCounts[channel] = (channelCounts[channel] || 0) + 1;
      });

      return Object.entries(channelCounts).map(([channel, count]) => ({
        channel,
        count
      }));
    } catch (error) {
      console.error('Error in getSessionsByChannel:', error);
      throw error;
    }
  }
};

export default sesionesService;